import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import { useAuthContext } from "/@/shared/contexts/AuthContext";
import { useOrdersList } from "@jasper/use-cases";
import {
  GetDashboardCountsDocument,
  GetDashboardLastDraftDocument,
} from "./dashboard.graphql";

export const ROWS_PER_TABLE = 5;

const getCurrentMonthRange = () => {
  const now = new Date();

  return {
    deliveredFrom: new Date(now.getFullYear(), now.getMonth(), 1).getTime(),
    deliveredTo: new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0,
      23,
      59,
      59
    ).getTime(),
  };
};

export const useDashboardData = () => {
  const { user } = useAuthContext();
  const dentistId = user?.id ?? "";
  const dentalOfficeId = user?.userGroup?.id ?? "";
  const byOffice = Boolean(dentalOfficeId && user?.managedUserGroup);
  const scope = { dentistId, dentalOfficeId, byOffice };
  const skip = !dentistId;

  const { deliveredFrom, deliveredTo } = useMemo(getCurrentMonthRange, []);

  const { data: drafts } = useQuery(GetDashboardLastDraftDocument, {
    variables: scope,
    skip,
    fetchPolicy: "network-only",
  });

  const { data: counts } = useQuery(GetDashboardCountsDocument, {
    variables: { ...scope, deliveredFrom, deliveredTo },
    skip,
    fetchPolicy: "cache-and-network",
  });

  // The dentist's own orders, or the whole office's for its manager
  const ordersListScope = {
    dentalOfficeId: dentalOfficeId || null,
    pageSize: ROWS_PER_TABLE,
    skip,
  };
  const ordersListFilter = byOffice ? {} : { dentistId };

  const recentOrders = useOrdersList({
    ...ordersListScope,
    filter: ordersListFilter,
    sort: "LAST_ACTIVITY",
  });

  const upcomingDeliveries = useOrdersList({
    ...ordersListScope,
    filter: { ...ordersListFilter, status: { excluded: ["DELIVERED"] } },
    sort: "DELIVERY_DATE",
  });

  return {
    deliveredFrom,
    lastDraft: (drafts?.officeLastDrafts ?? drafts?.lastDrafts)?.nodes[0],
    upcomingDeliveries,
    recentOrders,
    counts: {
      total: counts?.officeTotal ?? counts?.total ?? 0,
      waitingForAnswer:
        counts?.officeWaitingForAnswer ?? counts?.waitingForAnswer ?? 0,
      deliveredThisMonth:
        counts?.officeDeliveredThisMonth ?? counts?.deliveredThisMonth ?? 0,
      drafts: counts?.officeDrafts ?? counts?.drafts ?? 0,
      inProgress: counts?.officeInProgress ?? counts?.inProgress ?? 0,
    },
  };
};
